'use client'

import { useTranslations } from 'next-intl'

import { TRoomDetail, TSimpleReviewCount } from '@/entities/room'
import { useRouter } from '@/shared/i18n'
import {
  cn,
  useCommonDialog,
  useMultiScrollState,
  useSession,
} from '@/shared/lib'
import { Button, Icon } from '@/shared/ui'

import { Heading } from '../common'
import { OtherRooms, ROW_DISPLAY_COUNT } from './other-rooms'
import { Review } from './review'
import { SuperHostBadge } from './super-host-badge'
import { UserInformation } from './user-information'
import { TabValue } from '../../lib/tab'

type Props = Pick<
  TRoomDetail,
  'rid' | 'cid' | 'hostUser' | 'otherHostRooms'
> & {
  pageType?: 'detail' | 'preview'
  hostSimpleReview: TSimpleReviewCount[]
}

export const HostIntroduction = ({
  rid,
  cid,
  hostUser,
  otherHostRooms,
  hostSimpleReview,
  pageType = 'detail',
}: Props) => {
  const t = useTranslations('room_detail')

  const router = useRouter()

  const session = useSession()
  const isLoggedIn = !!session?.data

  const { registerScrollElement } = useMultiScrollState<HTMLElement>()

  const { openMoveToSignInDialog } = useCommonDialog()

  const totalRoomCount = otherHostRooms.length

  const handleChatButtonClick = () => {
    if (!isLoggedIn) {
      openMoveToSignInDialog()

      return
    }

    if (cid) {
      router.push(`/guest/chat/${cid}`)
    } else {
      router.push(`/guest/room/${rid}/contact-host`)
    }
  }

  return (
    <section
      ref={element => {
        if (element) {
          registerScrollElement(TabValue.HOST_INFORMATION, element)
        }
      }}
    >
      <Heading className="mb-5">{t('landlord_profile.title')}</Heading>

      <div className="rounded-sm bg-white shadow-[0px_1px_7px_0px_rgba(0,0,0,0.15)]">
        {hostUser.isSuperHost && <SuperHostBadge />}

        <div className="flex justify-between p-10">
          <div
            className={cn(
              'flex flex-col justify-between space-y-10 pr-10',
              totalRoomCount > ROW_DISPLAY_COUNT && 'space-y-13',
            )}
          >
            <UserInformation {...hostUser} />
            <Review hostSimpleReview={hostSimpleReview} />
          </div>

          <div className="w-[292px] space-y-10">
            <OtherRooms
              otherHostRooms={otherHostRooms}
              disabled={pageType === 'preview'}
            />
            <Button
              variant="outline"
              shape="pill"
              size="medium"
              fullWidth
              onClick={handleChatButtonClick}
              disabled={pageType === 'preview'}
            >
              {t('landlord_profile.button.chat')}
              <Icon name="arrow-right" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}
